// app/packages/platform-contract/src/signature/signature-capture.schema.ts
//
// Captured-signature DTOs that ride the modified host write routes and the
// internal capture / discard endpoints. Match design.md §6 (doc-signature-stubs).
import { z } from 'zod';

/** A single captured signature slot in a save payload. */
export const CapturedSignatureSchema = z.object({
  /** Slot label — matches FormConfigurationSignature.SignatureType (== DocumentImageType.DocumentImageTypeName). */
  signatureType: z.string().min(1).max(100),
  /** Stable per-slot id allocated client-side (Q2) — UPSERT key for spDocumentImage_Insert. */
  documentImageId: z.string().guid(),
  /** Base64-encoded PNG bytes; null when the slot is part of a manual-sign batch (Q4, Q10). */
  imageBase64: z.string().nullable(),
  /** Signer's EmployeeId — null on manual-sign (Q10). */
  employeeId: z.string().guid().nullable(),
  /** OrderID from FormConfigurationSignature; carried for SP parameter parity. */
  orderId: z.number().int().min(1).max(255),
  /** IsRequired copied from FormConfigurationSignature at slot-roster time. */
  isRequired: z.boolean(),
});

/** The fixed-shape signature block every modified host write route accepts. */
export const SignaturePayloadSchema = z.object({
  /** Resolved FormConfigurationId for this command + context. */
  formConfigurationId: z.string().guid(),
  /** Branch discriminator: 'digital' (canvas bytes per slot) or 'manual' (every slot unsigned, IsManuallySigned=1 on Document). */
  branch: z.enum(['digital', 'manual']),
  /**
   * One entry per visible required slot. For 'manual', every entry has
   * imageBase64=null and employeeId=null. May be empty when the resolved
   * FormConfiguration has zero FormConfigurationSignature rows — the host
   * still creates a Document tied to `formConfigurationId`, but no
   * DocumentImage rows. This handles legacy forms like initial machine
   * counts where a document is required but signatures aren't.
   */
  signatures: z.array(CapturedSignatureSchema),
});

/** UPSERT body for capture-time roundtrips (POST /api/{tool}/signature/capture). */
export const SignatureCaptureBody = z.object({
  sourceId: z.string().guid(),
  sourceTable: z.string().min(1).max(128),
  documentType: z.string().min(1).max(100),
  documentImageId: z.string().guid(),
  documentImageType: z.string().min(1).max(128),
  orderId: z.number().int().min(1).max(255),
  imageBase64: z.string(),
  employeeId: z.string().guid(),
  isRequired: z.boolean(),
  /**
   * Identifies which host write command's dialog this capture belongs to
   * (e.g. 'load' for CB Load). Read by the host handler's dynamic
   * `permission` resolver — RBAC runs before zod validation, so the
   * resolver reads this defensively off the raw body (unit-02-signature-
   * persistence-routes plan, Approach).
   */
  hostCommand: z.string().min(1).max(64),
});

/** Tear-down body for 409 STALE_RESOURCE + Cancel-after-sign (POST /api/{tool}/signature/discard). */
export const SignatureDiscardBody = z.object({
  documentImageIds: z.array(z.string().guid()).min(1),
  /** See `SignatureCaptureBody.hostCommand`. */
  hostCommand: z.string().min(1).max(64),
});

export type CapturedSignature = z.infer<typeof CapturedSignatureSchema>;
export type SignaturePayload = z.infer<typeof SignaturePayloadSchema>;
export type SignatureCaptureInput = z.infer<typeof SignatureCaptureBody>;
export type SignatureDiscardInput = z.infer<typeof SignatureDiscardBody>;
